// Authentication service with database integration
import { supabase } from '../config/supabase.js';
import bcrypt from 'bcryptjs';

const AUTH_TOKEN_KEY = 'admin_auth_token';
const SESSION_TIMEOUT = 24 * 60 * 60 * 1000; // 24 hours
const RESET_TOKEN_EXPIRY = 60 * 60 * 1000; // 1 hour

// Generate a secure random token
const generateToken = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

export const authService = {
  // Login with email and password
  async login(email, password) {
    try {
      // Find user by email
      const { data: user, error: userError } = await supabase
        .from('admin_users')
        .select('id, email, password_hash, full_name, is_active')
        .eq('email', email.toLowerCase().trim())
        .single();

      if (userError || !user) {
        return { success: false, error: 'Invalid email or password' };
      }

      // Check if user is active
      if (!user.is_active) {
        return { success: false, error: 'Account is inactive. Please contact administrator.' };
      }

      // Verify password
      const passwordMatch = await bcrypt.compare(password, user.password_hash);
      if (!passwordMatch) {
        return { success: false, error: 'Invalid email or password' };
      }

      // Update last login
      await supabase
        .from('admin_users')
        .update({ last_login: new Date().toISOString() })
        .eq('id', user.id);

      // Create session token
      const token = btoa(`${user.email}:${Date.now()}:${generateToken()}`);
      const sessionData = {
        token,
        email: user.email,
        userId: user.id,
        fullName: user.full_name,
        expiresAt: Date.now() + SESSION_TIMEOUT
      };
      
      sessionStorage.setItem(AUTH_TOKEN_KEY, JSON.stringify(sessionData));
      return { success: true, token, user: { email: user.email, fullName: user.full_name } };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'An error occurred during login. Please try again.' };
    }
  },

  // Check if user is authenticated
  isAuthenticated() {
    const sessionData = sessionStorage.getItem(AUTH_TOKEN_KEY);
    if (!sessionData) return false;

    try {
      const data = JSON.parse(sessionData);
      if (Date.now() > data.expiresAt) {
        this.logout();
        return false;
      }
      return true;
    } catch (e) {
      return false;
    }
  },

  // Get current user
  getCurrentUser() {
    const sessionData = sessionStorage.getItem(AUTH_TOKEN_KEY);
    if (!sessionData) return null;

    try {
      const data = JSON.parse(sessionData);
      if (Date.now() > data.expiresAt) {
        this.logout();
        return null;
      }
      return { 
        email: data.email, 
        fullName: data.fullName,
        userId: data.userId 
      };
    } catch (e) {
      return null;
    }
  },

  // Logout
  logout() {
    sessionStorage.removeItem(AUTH_TOKEN_KEY);
  },

  // Forgot password - generate reset token
  async forgotPassword(email) {
    try {
      // Find user by email
      const { data: user, error: userError } = await supabase
        .from('admin_users')
        .select('id, email, is_active')
        .eq('email', email.toLowerCase().trim())
        .single();

      // Don't reveal if user exists or not (security best practice)
      if (userError || !user || !user.is_active) {
        // Still return success to prevent email enumeration
        return { success: true, message: 'If an account exists with this email, a reset link has been sent.' };
      }

      // Generate reset token
      const resetToken = generateToken();
      const expiresAt = new Date(Date.now() + RESET_TOKEN_EXPIRY);

      // Delete any existing reset tokens for this user
      await supabase
        .from('password_reset_tokens')
        .delete()
        .eq('user_id', user.id)
        .eq('used', false);

      // Insert new reset token
      if (process.env.NODE_ENV === 'development') {
        console.log('Inserting token into database:', resetToken);
        console.log('Token length:', resetToken.length);
      }

      const { data: insertedToken, error: tokenError } = await supabase
        .from('password_reset_tokens')
        .insert({
          user_id: user.id,
          token: resetToken,
          expires_at: expiresAt.toISOString(),
          used: false
        })
        .select()
        .single();

      if (tokenError) {
        console.error('Error creating reset token:', tokenError);
        return { success: false, error: 'Failed to create reset token. Please try again.' };
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('Token inserted successfully:', insertedToken);
      }

      // In development, always store token in sessionStorage for testing
      // Use the token from the database insert to ensure exact match
      if (process.env.NODE_ENV === 'development') {
        const tokenToStore = insertedToken?.token || resetToken;
        sessionStorage.setItem('password_reset_token', JSON.stringify({
          token: tokenToStore,
          email: user.email,
          expiresAt: expiresAt.getTime()
        }));
        console.log('Development: Token stored in sessionStorage:', tokenToStore);
        console.log('Development: Token from DB insert:', insertedToken?.token);
        console.log('Development: Tokens match:', tokenToStore === resetToken);
      }

      // Send password reset email via Supabase Edge Function
      // Use the token from database to ensure exact match
      const tokenForEmail = insertedToken?.token || resetToken;
      
      try {
        const baseUrl = window.location.origin;
        const resetUrl = `${baseUrl}/admin/reset-password?token=${encodeURIComponent(tokenForEmail)}`;
        
        const { data: emailResult, error: emailError } = await supabase.functions.invoke('send-password-reset-email', {
          body: {
            email: user.email,
            resetToken: tokenForEmail,
            userName: user.full_name || user.email.split('@')[0],
            resetUrl: resetUrl,
          },
        });
        
        if (emailError || !emailResult?.success) {
          console.error('Error sending email:', emailError || emailResult);
          // Still return success to user (don't reveal email sending failure)
          // Log the error for debugging
        }
      } catch (emailError) {
        console.error('Error calling email function:', emailError);
        // Still return success to user (don't reveal email sending failure)
      }
      
      return { success: true, message: 'If an account exists with this email, a reset link has been sent.' };
    } catch (error) {
      console.error('Forgot password error:', error);
      return { success: false, error: 'An error occurred. Please try again.' };
    }
  },

  // Verify reset token
  async verifyResetToken(token) {
    try {
      // Vue Router automatically decodes URL parameters, but handle both cases
      let decodedToken = token;
      try {
        // Try decoding in case it's double-encoded
        decodedToken = decodeURIComponent(token);
      } catch (e) {
        // If already decoded, use as-is
        decodedToken = token;
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Verifying token (original):', token);
        console.log('Verifying token (decoded):', decodedToken);
        console.log('Token length:', decodedToken.length);
      }

      // First, check if any tokens exist (for debugging)
      if (process.env.NODE_ENV === 'development') {
        const { data: allTokens } = await supabase
          .from('password_reset_tokens')
          .select('token, used, expires_at')
          .limit(5);
        console.log('All tokens in DB (last 5):', allTokens);
      }

      const { data: resetToken, error } = await supabase
        .from('password_reset_tokens')
        .select('id, user_id, expires_at, used, token')
        .eq('token', decodedToken)
        .eq('used', false)
        .maybeSingle(); // Use maybeSingle() instead of single() to avoid error on 0 rows

      if (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Token verification error:', error);
        }
        return { valid: false };
      }

      if (!resetToken) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Token not found in database');
          console.log('Searched for:', decodedToken);
        }
        return { valid: false };
      }

      // Check if token is expired
      const expiresAt = new Date(resetToken.expires_at);
      if (expiresAt < new Date()) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Token expired. Expires at:', expiresAt, 'Current time:', new Date());
        }
        return { valid: false };
      }

      // Get user email
      const { data: user } = await supabase
        .from('admin_users')
        .select('email')
        .eq('id', resetToken.user_id)
        .single();

      return { 
        valid: true, 
        email: user?.email || '',
        userId: resetToken.user_id,
        tokenId: resetToken.id
      };
    } catch (error) {
      console.error('Verify reset token error:', error);
      return { valid: false };
    }
  },

  // Reset password
  async resetPassword(token, newPassword) {
    try {
      // Verify token
      const verification = await this.verifyResetToken(token);
      if (!verification.valid) {
        return { success: false, error: 'Invalid or expired reset token' };
      }

      // Validate password length
      if (newPassword.length < 6) {
        return { success: false, error: 'Password must be at least 6 characters long' };
      }

      // Hash new password
      const saltRounds = 10;
      const passwordHash = await bcrypt.hash(newPassword, saltRounds);

      // Update password in database
      const { error: updateError } = await supabase
        .from('admin_users')
        .update({ password_hash: passwordHash })
        .eq('id', verification.userId);

      if (updateError) {
        console.error('Error updating password:', updateError);
        return { success: false, error: 'Failed to reset password. Please try again.' };
      }

      // Mark token as used
      await supabase
        .from('password_reset_tokens')
        .update({ used: true })
        .eq('token', token);

      // Clean up old tokens for this user
      await supabase
        .from('password_reset_tokens')
        .delete()
        .eq('user_id', verification.userId)
        .eq('used', true);

      // Clear session storage token if it exists
      sessionStorage.removeItem('password_reset_token');
      
      return { success: true, message: 'Password reset successfully. You can now login with your new password.' };
    } catch (error) {
      console.error('Reset password error:', error);
      return { success: false, error: 'An error occurred. Please try again.' };
    }
  }
};
